import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileText, Plus, Download, Trash2, Edit3, X, Search, Eye, PenTool } from "lucide-react";
import { useDriveAuth } from "../context/DriveAuthContext";
import {
  fetchTemplates,
  fetchContracts,
  saveTemplate,
  deleteTemplate,
  extractPlaceholders,
  buildValues,
  generateAndArchiveContract,
  finalizeSignedContract,
  contractIsFullySigned,
} from "../lib/contractEngine";
import { fetchArtists, fetchCustomers, fetchExternals, fetchFeatures, fetchCreators } from "../lib/driveClient";
import { createSignatureRequest } from "../lib/signatureClient";
import { supabase } from "../../lib/supabase";

const CATEGORIES = [
  { value: "management", label: "Management" },
  { value: "label", label: "Label" },
  { value: "nda", label: "NDA" },
  { value: "license", label: "Lizenz" },
  { value: "feature", label: "Feature" },
  { value: "creator", label: "Creator" },
  { value: "poa", label: "Vollmacht" },
  { value: "producer", label: "Produzent" },
  { value: "cooperation", label: "Kooperation" },
  { value: "other", label: "Sonstige" },
];

const STATUS_COLORS = {
  draft: "text-zinc-400 border-zinc-700",
  sent: "text-blue-400 border-blue-500/40",
  signed: "text-emerald-400 border-emerald-500/40",
  archived: "text-white border-white/20",
  cancelled: "text-zinc-600 border-zinc-800",
};

export default function DriveContracts() {
  const { isStaff } = useDriveAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("contracts");
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [artists, setArtists] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [externals, setExternals] = useState([]);
  const [features, setFeatures] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [editTpl, setEditTpl] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [t, c, a, cu, ex, fe, cr] = await Promise.all([
        fetchTemplates(),
        fetchContracts(),
        fetchArtists().catch(() => []),
        fetchCustomers().catch(() => []),
        fetchExternals().catch(() => []),
        fetchFeatures().catch(() => []),
        fetchCreators().catch(() => []),
      ]);
      setTemplates(t);
      setContracts(c);
      setArtists(a);
      setCustomers(cu);
      setExternals(ex);
      setFeatures(fe);
      setCreators(cr);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Handle sidebar quick-action: ?action=new opens wizard
  useEffect(() => {
    if (searchParams.get("action") === "new") {
      setShowWizard(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const filtered = contracts.filter(c =>
    q ? (
      c.title?.toLowerCase().includes(q.toLowerCase()) ||
      c.drive_artists?.artist_name?.toLowerCase().includes(q.toLowerCase()) ||
      c.drive_customers?.name?.toLowerCase().includes(q.toLowerCase()) ||
      c.drive_externals?.full_name?.toLowerCase().includes(q.toLowerCase()) ||
      c.drive_features?.artist_name?.toLowerCase().includes(q.toLowerCase())
    ) : true
  );

  const [signLinks, setSignLinks] = useState(null); // { contractTitle, entries: [{label, url}] }

  const requestSignatures = async (contract) => {
    try {
      // Detect roles based on which parties are linked.
      // Feature-Vertrag (3-way) = Label + Main-Artist + Feature-Artist
      // Creator-Vertrag (2-way)  = Management + Creator
      const roles = [];
      const hasFeature = contract.feature_id;
      const hasArtist = contract.artist_id;
      const hasCreator = contract.creator_id;
      if (hasCreator) {
        roles.push({ role: "manager", label: "Straßenwert Management" });
        roles.push({ role: "creator", label: contract?.drive_creators?.creator_name || "Creator" });
      } else if (hasFeature && hasArtist) {
        roles.push({ role: "label", label: "Straßenwert Records" });
        roles.push({ role: "artist", label: contract?.drive_artists?.artist_name || "Haupt-Künstler" });
        roles.push({ role: "feature", label: contract?.drive_features?.artist_name || "Feature-Künstler" });
      } else if (hasFeature) {
        roles.push({ role: "label", label: "Straßenwert Records" });
        roles.push({ role: "feature", label: contract?.drive_features?.artist_name || "Feature-Künstler" });
      } else if (contract.external_id) {
        roles.push({ role: "label", label: "Straßenwert Records" });
        roles.push({ role: "external", label: contract?.drive_externals?.full_name || "Vertragspartner" });
      } else if (contract.customer_id) {
        roles.push({ role: "label", label: "Straßenwert Records" });
        roles.push({ role: "customer", label: contract?.drive_customers?.name || "Vertragspartner" });
      } else {
        roles.push({ role: contract.category === "management" ? "manager" : "label", label: "Straßenwert" });
        roles.push({ role: "artist", label: contract?.drive_artists?.artist_name || "Künstler" });
      }
      const created = [];
      for (const r of roles) {
        const s = await createSignatureRequest(contract.id, r.role, r.label);
        created.push({ ...s, ...r });
      }
      const origin = window.location.origin;
      const entries = created.map(s => ({ label: s.label, role: s.role, url: `${origin}/sign/${s.token}` }));
      // Copy to clipboard
      const clipTxt = entries.map(e => `${e.label}: ${e.url}`).join("\n\n");
      await navigator.clipboard.writeText(clipTxt).catch(() => {});
      // Update status to sent
      await supabase.from("drive_contracts").update({ status: "sent" }).eq("id", contract.id);
      await load();
      setSignLinks({ contractTitle: contract.title, entries });
      toast.success(`${entries.length} Signatur-Links erstellt & kopiert`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteContract = async (contract) => {
    if (!window.confirm(`Vertrag „${contract.title}" wirklich löschen?\n\nDie PDF wird auch aus dem Ordner Finanzen/Verträge entfernt.`)) return;
    try {
      if (contract.storage_path) {
        await supabase.storage.from("drive-files").remove([contract.storage_path]);
      }
      if (contract.file_id) {
        await supabase.from("drive_files").delete().eq("id", contract.file_id);
      }
      const { error } = await supabase.from("drive_contracts").delete().eq("id", contract.id);
      if (error) throw error;
      toast.success("Vertrag gelöscht");
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Ensures signed contract PDFs get finalized (embedded signatures) before opening
  const resolveContractPath = async (contract) => {
    // Wenn bereits finalisiert → direkt zurückgeben
    if (contract.signed_storage_path) return contract.signed_storage_path;
    // Prüfen ob alle unterschrieben haben
    try {
      const fully = await contractIsFullySigned(contract.id);
      if (fully) {
        toast("Alle Unterschriften erkannt — finalisiere PDF…");
        const { signedPath } = await finalizeSignedContract(contract.id);
        await load();
        return signedPath;
      }
    } catch (e) {
      console.warn("finalize check failed", e);
    }
    return contract.storage_path;
  };

  const previewContract = async (contract) => {
    try {
      const path = await resolveContractPath(contract);
      const { data, error } = await supabase.storage.from("drive-files").createSignedUrl(path, 300);
      if (error) throw error;
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const downloadContract = async (contract) => {
    try {
      const path = await resolveContractPath(contract);
      const suffix = path.endsWith("-SIGNED.pdf") ? "-SIGNED" : "";
      const { data, error } = await supabase.storage.from("drive-files").createSignedUrl(path, 60, { download: contract.title + suffix + ".pdf" });
      if (error) throw error;
      const a = document.createElement("a");
      a.href = data.signedUrl;
      a.download = contract.title + suffix + ".pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-3 text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold">
            <span className="block w-8 h-px bg-[#ff0033] shadow-[0_0_8px_#ff0033]" />
            Verträge
          </div>
          <h1 className="font-display font-black uppercase tracking-tighter leading-none text-3xl md:text-5xl">
            Vertragsgenerator
          </h1>
          <p className="mt-3 text-sm text-zinc-500 max-w-2xl">
            Vorlagen verwalten und automatisch ausgefüllte Verträge erstellen. Fertige Verträge landen als PDF im Ordner <span className="text-white">Finanzen → Verträge</span>.
          </p>
        </div>
        {isStaff && (
          <button
            onClick={() => setShowWizard(true)}
            data-testid="btn-new-contract"
            className="group inline-flex items-center gap-2 bg-white text-black px-5 py-3 text-[10px] font-bold tracking-[0.25em] uppercase hover:bg-[#ff0033] hover:text-white transition-colors"
          >
            <Plus size={13} /> Neuer Vertrag
          </button>
        )}
      </motion.div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/[0.06]">
        <TabBtn active={tab==="contracts"} onClick={()=>setTab("contracts")} testid="tab-contracts">Verträge ({contracts.length})</TabBtn>
        <TabBtn active={tab==="templates"} onClick={()=>setTab("templates")} testid="tab-templates">Vorlagen ({templates.length})</TabBtn>
      </div>

      {tab === "contracts" ? (
        <>
          <div className="relative">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Verträge durchsuchen…" data-testid="contract-search" className="w-full bg-[#0A0A0A] border border-white/10 pl-11 pr-4 py-3 text-sm text-white outline-none focus:border-[#ff0033]/40" />
          </div>

          {loading ? (
            <div className="p-10 text-center text-sm text-zinc-500">Lade…</div>
          ) : filtered.length === 0 ? (
            <div className="bg-[#0A0A0A] border border-white/[0.06] p-10 text-center text-sm text-zinc-500">
              Noch keine Verträge. Erstelle den ersten oben rechts.
            </div>
          ) : (
            <div className="bg-[#0A0A0A] border border-white/[0.06] divide-y divide-white/[0.04]">
              {filtered.map((c) => (
                <div key={c.id} data-testid={`contract-${c.id}`} className="p-4 md:px-6 flex items-center justify-between gap-4 hover:bg-white/[0.02]">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <FileText size={18} className="text-[#ff0033] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-white font-medium truncate">{c.title}</div>
                      <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-600 mt-0.5">
                        {c.drive_artists?.artist_name || c.drive_features?.artist_name || c.drive_customers?.name || c.drive_externals?.full_name || "—"}
                        {c.drive_features?.artist_name && c.drive_artists?.artist_name ? ` × ${c.drive_features.artist_name}` : ""}
                        {" · "}{new Date(c.created_at).toLocaleDateString("de-DE")}
                        {c.category && <> · {c.category}</>}
                      </div>
                    </div>
                  </div>
                  <span className={`text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-1 border ${STATUS_COLORS[c.status]}`}>{c.status}</span>
                  <button
                    onClick={() => requestSignatures(c)}
                    data-testid={`sign-${c.id}`}
                    title="Zur Unterschrift senden"
                    className="p-2 text-zinc-400 hover:text-[#ff0033] hover:bg-[#ff0033]/[0.08] transition-colors"
                  >
                    <PenTool size={14} />
                  </button>
                  <button
                    onClick={() => previewContract(c)}
                    data-testid={`preview-${c.id}`}
                    title="Vorschau"
                    className="p-2 text-zinc-400 hover:text-[#ff0033] hover:bg-[#ff0033]/[0.08] transition-colors"
                  >
                    <Eye size={15} />
                  </button>
                  <button
                    onClick={() => downloadContract(c)}
                    data-testid={`download-${c.id}`}
                    title="Herunterladen"
                    className="p-2 text-zinc-400 hover:text-white hover:bg-white/[0.05] transition-colors"
                  >
                    <Download size={14} />
                  </button>
                  <button
                    onClick={() => deleteContract(c)}
                    data-testid={`delete-contract-${c.id}`}
                    title="Löschen"
                    className="p-2 text-zinc-400 hover:text-[#ff0033] hover:bg-[#ff0033]/[0.08] transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <TemplatesTab
          templates={templates}
          onEdit={(t)=>setEditTpl(t)}
          onNew={()=>setEditTpl({ name:"", category:"other", body_markdown:"", description:"" })}
          onDelete={async(id)=>{
            if(!window.confirm("Vorlage löschen?")) return;
            await deleteTemplate(id); toast.success("Gelöscht"); await load();
          }}
        />
      )}

      {/* Modals */}
      {showWizard && (
        <ContractWizard
          templates={templates}
          artists={artists}
          customers={customers}
          externals={externals}
          features={features}
          creators={creators}
          onClose={()=>setShowWizard(false)}
          onDone={async()=>{ setShowWizard(false); await load(); }}
        />
      )}
      {editTpl && (
        <TemplateEditor
          initial={editTpl}
          onClose={()=>setEditTpl(null)}
          onSaved={async()=>{ setEditTpl(null); await load(); }}
        />
      )}
      {signLinks && (
        <SignLinksModal
          data={signLinks}
          onClose={() => setSignLinks(null)}
        />
      )}
    </div>
  );
}

const TabBtn = ({ active, children, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`px-5 py-3 text-[11px] font-bold tracking-[0.25em] uppercase transition-colors border-b-2 -mb-px ${active ? "border-[#ff0033] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>
    {children}
  </button>
);

// ─── Templates Tab ───────────────────────────────────────────────
const TemplatesTab = ({ templates, onEdit, onNew, onDelete }) => (
  <div className="space-y-4">
    <div className="flex justify-end">
      <button onClick={onNew} data-testid="btn-new-template" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white px-4 py-2 text-[10px] font-bold tracking-[0.25em] uppercase transition-colors">
        <Plus size={12} /> Neue Vorlage
      </button>
    </div>
    <div className="grid md:grid-cols-2 gap-3">
      {templates.map((t) => (
        <div key={t.id} data-testid={`tpl-${t.id}`} className="bg-[#0A0A0A] border border-white/[0.06] p-5 hover:border-[#ff0033]/30 transition-colors">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-1.5">{t.category}</div>
              <div className="font-display font-black uppercase text-lg tracking-tight truncate">{t.name}</div>
              {t.description && <div className="text-xs text-zinc-500 mt-2 line-clamp-2">{t.description}</div>}
              <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-600 mt-3">
                {extractPlaceholders(t.body_markdown).length} Platzhalter
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={()=>onEdit(t)} data-testid={`edit-tpl-${t.id}`} className="p-2 text-zinc-400 hover:text-white"><Edit3 size={13} /></button>
              <button onClick={()=>onDelete(t.id)} data-testid={`del-tpl-${t.id}`} className="p-2 text-zinc-400 hover:text-[#ff0033]"><Trash2 size={13} /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Template Editor Modal ───────────────────────────────────────
const TemplateEditor = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const placeholders = extractPlaceholders(form.body_markdown || "");

  const save = async () => {
    if (!form.name || !form.body_markdown) { toast.error("Name & Text sind Pflicht"); return; }
    setBusy(true);
    try {
      await saveTemplate(form);
      toast.success("Vorlage gespeichert");
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-4xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] sticky top-0 bg-[#0A0A0A]">
          <div className="font-display font-black uppercase text-lg">{initial.id ? "Vorlage bearbeiten" : "Neue Vorlage"}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">Name *</span>
              <input required data-testid="tpl-name" value={form.name} onChange={(e)=>setForm(f=>({...f,name:e.target.value}))} className="tpl-input" />
            </label>
            <label className="block">
              <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">Kategorie</span>
              <select value={form.category} onChange={(e)=>setForm(f=>({...f,category:e.target.value}))} data-testid="tpl-cat" className="tpl-input bg-transparent">
                {CATEGORIES.map(c => <option key={c.value} value={c.value} className="bg-[#0A0A0A]">{c.label}</option>)}
              </select>
            </label>
            <label className="block md:col-span-2">
              <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">Beschreibung</span>
              <input data-testid="tpl-desc" value={form.description||""} onChange={(e)=>setForm(f=>({...f,description:e.target.value}))} className="tpl-input" />
            </label>
            <label className="block md:col-span-2">
              <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">
                Vertragstext (Markdown, Platzhalter <code className="text-[#ff0033]">{`{{feld_name}}`}</code>)
              </span>
              <textarea data-testid="tpl-body" rows={18} value={form.body_markdown} onChange={(e)=>setForm(f=>({...f,body_markdown:e.target.value}))} className="tpl-input font-mono text-xs resize-y" />
            </label>
            {placeholders.length > 0 && (
              <div className="md:col-span-2">
                <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">Erkannte Platzhalter</div>
                <div className="flex flex-wrap gap-1.5">
                  {placeholders.map(p => <span key={p} className="text-[10px] tracking-wider font-mono px-2 py-1 bg-[#ff0033]/10 text-[#ff0033] border border-[#ff0033]/30">{`{{${p}}}`}</span>)}
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/[0.06]">
            <button onClick={onClose} className="text-xs tracking-[0.25em] uppercase text-zinc-500 hover:text-white">Abbrechen</button>
            <button onClick={save} disabled={busy} data-testid="tpl-save" className="bg-white text-black px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:bg-[#ff0033] hover:text-white transition-colors disabled:opacity-50">
              {busy ? "Speichere…" : "Speichern"}
            </button>
          </div>
        </div>
      </div>
      <style>{`
        .tpl-input { width:100%; background:transparent; border:1px solid rgba(255,255,255,0.1); padding:10px 12px; color:#fff; font-size:14px; outline:none; }
        .tpl-input:focus { border-color: rgba(255,0,51,0.5); }
      `}</style>
    </div>
  );
};

// ─── Contract Wizard ─────────────────────────────────────────────
const ContractWizard = ({ templates, artists, customers, externals, features, creators, onClose, onDone }) => {
  const [step, setStep] = useState(1);
  const [templateId, setTemplateId] = useState("");
  const [partyKind, setPartyKind] = useState(""); // "artist" | "customer" | "external" | "feature" | "creator"
  const [artistId, setArtistId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [externalId, setExternalId] = useState("");
  const [featureId, setFeatureId] = useState("");
  const [creatorId, setCreatorId] = useState("");
  const [title, setTitle] = useState("");
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);

  const template = templates.find(t => t.id === templateId);
  const artist = artists.find(a => a.id === artistId);
  const customer = customers.find(c => c.id === customerId);
  const external = externals.find(x => x.id === externalId);
  const feature = (features || []).find(f => f.id === featureId);
  const creator = (creators || []).find(c => c.id === creatorId);

  const placeholders = template ? extractPlaceholders(template.body_markdown) : [];

  const isFeatureTpl = template && template.category === "feature";
  const isCreatorTpl = template && (template.category === "creator" || template.category === "poa");

  // Auto-preselect party kind based on template category
  useEffect(() => {
    if (!template) return;
    if (isFeatureTpl && !partyKind) setPartyKind("feature");
    else if (isCreatorTpl && !partyKind) setPartyKind("creator");
    else if (["nda", "cooperation"].includes(template.category) && !partyKind) setPartyKind("external");
    else if (template.category === "license" && !partyKind) setPartyKind("customer");
     
  }, [templateId]);

  const initValues = () => {
    if (!template) return;
    const auto = buildValues({ artist, customer, external, feature, creator });
    const merged = {};
    for (const p of placeholders) merged[p] = auto[p] ?? "";
    setValues(merged);
    const mainName = creator?.creator_name || feature?.artist_name || artist?.artist_name || customer?.name || external?.full_name || "Vorlage";
    const featureSuffix = (isFeatureTpl && feature && artist) ? ` × ${feature.artist_name}` : "";
    const base = feature && artist ? artist.artist_name : mainName;
    setTitle(`${template.name} — ${base}${featureSuffix}`);
  };

  const submit = async () => {
    if (!template) return;
    setBusy(true);
    try {
      await generateAndArchiveContract({
        template,
        values,
        title,
        artist_id: artistId || null,
        customer_id: customerId || null,
        external_id: externalId || null,
        feature_id: featureId || null,
        creator_id: creatorId || null,
        category: template.category,
      });
      toast.success("Vertrag erstellt & im CRM gespeichert");
      onDone();
    } catch (e) {
      toast.error(e.message || "Erstellung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  // Für Feature-Vertrag müssen sowohl Feature als auch Haupt-Artist gewählt sein
  const partySelected = isFeatureTpl
    ? (featureId && artistId)
    : (artistId || customerId || externalId || featureId || creatorId);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] sticky top-0 bg-[#0A0A0A]">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">Schritt {step} / 3</div>
            <div className="font-display font-black uppercase text-lg mt-1">Neuen Vertrag erstellen</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          {step === 1 && (
            <>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Vorlage wählen</div>
              <div className="grid md:grid-cols-2 gap-2">
                {templates.map(t => (
                  <button
                    key={t.id}
                    data-testid={`wiz-tpl-${t.id}`}
                    onClick={()=>setTemplateId(t.id)}
                    className={`text-left border p-4 transition-all ${templateId===t.id ? "border-[#ff0033] bg-[#ff0033]/[0.05]" : "border-white/10 hover:border-white/25"}`}
                  >
                    <div className="text-[9px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-1">{t.category}</div>
                    <div className="font-display font-black uppercase tracking-tight">{t.name}</div>
                    {t.description && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{t.description}</div>}
                  </button>
                ))}
              </div>
              {templates.length === 0 && <div className="text-sm text-zinc-500">Keine Vorlagen. Erstelle zuerst eine unter Vorlagen.</div>}
            </>
          )}
          {step === 2 && (
            <>
              {isFeatureTpl ? (
                <>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Feature-Vertrag · 3 Parteien</div>
                  <div className="text-xs text-zinc-500 mb-2">
                    Ein Feature-Vertrag wird zwischen <span className="text-white">Label</span>, <span className="text-white">Haupt-Künstler</span> und <span className="text-[#ff0033]">Feature-Künstler</span> geschlossen. Wähle beide Künstler aus.
                  </div>
                  <label className="block">
                    <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">Haupt-Künstler (Straßenwert-Roster)</span>
                    <select data-testid="wiz-main-artist" value={artistId} onChange={(e)=>setArtistId(e.target.value)} className="wiz-input">
                      <option value="" className="bg-[#0A0A0A]">— Wählen —</option>
                      {artists.map(a=><option key={a.id} value={a.id} className="bg-[#0A0A0A]">{a.artist_name}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">Feature-Künstler</span>
                    <select data-testid="wiz-feature" value={featureId} onChange={(e)=>setFeatureId(e.target.value)} className="wiz-input">
                      <option value="" className="bg-[#0A0A0A]">— Wählen —</option>
                      {(features || []).map(f=><option key={f.id} value={f.id} className="bg-[#0A0A0A]">{f.artist_name} ({f.commission_percent ?? 40}%)</option>)}
                    </select>
                  </label>
                </>
              ) : (
                <>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Wen betrifft der Vertrag?</div>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { k: "artist", label: "Künstler" },
                      { k: "feature", label: "Feature" },
                      { k: "creator", label: "Creator" },
                      { k: "customer", label: "Kunde" },
                      { k: "external", label: "Extern" },
                    ].map(o => (
                      <button
                        key={o.k}
                        data-testid={`wiz-partykind-${o.k}`}
                        onClick={() => { setPartyKind(o.k); setArtistId(""); setCustomerId(""); setExternalId(""); setFeatureId(""); setCreatorId(""); }}
                        className={`p-3 text-[10px] font-bold tracking-[0.2em] uppercase border transition-colors ${
                          partyKind===o.k ? "border-[#ff0033] bg-[#ff0033]/[0.05] text-white" : "border-white/10 text-zinc-400 hover:border-white/25 hover:text-white"
                        }`}
                      >{o.label}</button>
                    ))}
                  </div>
                  {partyKind === "artist" && (
                    <label className="block">
                      <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">Künstler auswählen</span>
                      <select data-testid="wiz-artist" value={artistId} onChange={(e)=>setArtistId(e.target.value)} className="wiz-input">
                        <option value="" className="bg-[#0A0A0A]">— Wählen —</option>
                        {artists.map(a=><option key={a.id} value={a.id} className="bg-[#0A0A0A]">{a.artist_name}</option>)}
                      </select>
                    </label>
                  )}
                  {partyKind === "feature" && (
                    <label className="block">
                      <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">Feature-Künstler auswählen</span>
                      <select data-testid="wiz-feature-single" value={featureId} onChange={(e)=>setFeatureId(e.target.value)} className="wiz-input">
                        <option value="" className="bg-[#0A0A0A]">— Wählen —</option>
                        {(features || []).map(f=><option key={f.id} value={f.id} className="bg-[#0A0A0A]">{f.artist_name}</option>)}
                      </select>
                    </label>
                  )}
                  {partyKind === "creator" && (
                    <label className="block">
                      <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">Creator auswählen</span>
                      <select data-testid="wiz-creator" value={creatorId} onChange={(e)=>setCreatorId(e.target.value)} className="wiz-input">
                        <option value="" className="bg-[#0A0A0A]">— Wählen —</option>
                        {(creators || []).map(c=><option key={c.id} value={c.id} className="bg-[#0A0A0A]">{c.creator_name} ({c.commission_percent ?? 30}%)</option>)}
                      </select>
                    </label>
                  )}
                  {partyKind === "customer" && (
                    <label className="block">
                      <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">Kunde auswählen</span>
                      <select data-testid="wiz-customer" value={customerId} onChange={(e)=>setCustomerId(e.target.value)} className="wiz-input">
                        <option value="" className="bg-[#0A0A0A]">— Wählen —</option>
                        {customers.map(c=><option key={c.id} value={c.id} className="bg-[#0A0A0A]">{c.name}</option>)}
                      </select>
                    </label>
                  )}
                  {partyKind === "external" && (
                    <label className="block">
                      <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">Externen Kontakt auswählen</span>
                      <select data-testid="wiz-external" value={externalId} onChange={(e)=>setExternalId(e.target.value)} className="wiz-input">
                        <option value="" className="bg-[#0A0A0A]">— Wählen —</option>
                        {externals.map(x=><option key={x.id} value={x.id} className="bg-[#0A0A0A]">{x.full_name}{x.relation ? ` (${x.relation})` : ""}</option>)}
                      </select>
                    </label>
                  )}
                </>
              )}
            </>
          )}
          {step === 3 && template && (
            <>
              <label className="block">
                <span className="block text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">Vertrags-Titel</span>
                <input data-testid="wiz-title" value={title} onChange={(e)=>setTitle(e.target.value)} className="wiz-input" />
              </label>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold pt-2">Platzhalter befüllen</div>
              <div className="grid md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
                {placeholders.map(p => (
                  <label key={p} className="block">
                    <span className="block text-[9px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-1.5">{`{{${p}}}`}</span>
                    <input data-testid={`wiz-ph-${p}`} value={values[p] || ""} onChange={(e)=>setValues(v=>({...v,[p]:e.target.value}))} className="wiz-input" />
                  </label>
                ))}
              </div>
              <div className="text-xs text-zinc-500 pt-2">
                {isFeatureTpl
                  ? <>Der Feature-Vertrag wird als PDF erstellt und sowohl dem <span className="text-white">Haupt-Künstler</span> als auch dem <span className="text-[#ff0033]">Feature-Künstler</span> im CRM zugeordnet. Beim Versenden werden automatisch <span className="text-white">3 Signatur-Links</span> (Label, Haupt-Artist, Feature) erzeugt.</>
                  : <>Der Vertrag wird als PDF erstellt, direkt heruntergeladen und dem <span className="text-white">{partyKind === "customer" ? "Kunden" : partyKind === "external" ? "externen Kontakt" : partyKind === "feature" ? "Feature-Künstler" : "Künstler"}</span> im CRM zugeordnet.</>
                }
              </div>
            </>
          )}
          <div className="flex items-center justify-between gap-3 pt-4 border-t border-white/[0.06]">
            <button
              onClick={()=>step>1 ? setStep(step-1) : onClose()}
              className="text-xs tracking-[0.25em] uppercase text-zinc-500 hover:text-white"
            >
              {step > 1 ? "← Zurück" : "Abbrechen"}
            </button>
            {step < 3 ? (
              <button
                data-testid="wiz-next"
                disabled={(step===1 && !templateId) || (step===2 && !partySelected)}
                onClick={()=>{ if(step===2) initValues(); setStep(step+1); }}
                className="bg-white text-black px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:bg-[#ff0033] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Weiter →
              </button>
            ) : (
              <button
                data-testid="wiz-generate"
                disabled={busy}
                onClick={submit}
                className="bg-[#ff0033] text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:shadow-[0_0_24px_rgba(255,0,51,0.4)] transition-all disabled:opacity-50"
              >
                {busy ? "Generiere PDF…" : "Vertrag erstellen"}
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`
        .wiz-input { width:100%; background:transparent; border:1px solid rgba(255,255,255,0.1); padding:10px 12px; color:#fff; font-size:14px; outline:none; }
        .wiz-input:focus { border-color: rgba(255,0,51,0.5); }
      `}</style>
    </div>
  );
};

// ─── Signature Links Modal (DocuSign-style link distribution) ────
const SignLinksModal = ({ data, onClose }) => {
  const copyOne = async (url) => {
    await navigator.clipboard.writeText(url);
    toast.success("Link kopiert");
  };
  const copyAll = async () => {
    const txt = data.entries.map(e => `${e.label}: ${e.url}`).join("\n\n");
    await navigator.clipboard.writeText(txt);
    toast.success("Alle Links kopiert");
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-[#ff0033]/40 max-w-2xl w-full p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-1">Signatur-Anfragen erstellt ✓</div>
            <div className="font-display font-black uppercase text-xl truncate max-w-md">{data.contractTitle}</div>
            <div className="text-xs text-zinc-500 mt-1">Sende jeder Partei den zugehörigen Link. Jeder Link ist einmalig und funktioniert genau ein Mal.</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          {data.entries.map((e, i) => (
            <div key={i} className="bg-[#0F0F0F] border border-white/[0.06] p-4 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">{e.role.toUpperCase()}</div>
                <div className="text-sm text-white truncate">{e.label}</div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={e.url}
                  data-testid={`sign-link-${i}`}
                  onFocus={(ev) => ev.target.select()}
                  className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-xs text-zinc-300 font-mono outline-none focus:border-[#ff0033]/40"
                />
                <button
                  onClick={() => copyOne(e.url)}
                  data-testid={`copy-sign-${i}`}
                  className="inline-flex items-center gap-1.5 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white px-3 py-2 text-[10px] font-bold tracking-[0.2em] uppercase"
                >
                  Kopieren
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-white/[0.06]">
          <button onClick={copyAll} data-testid="copy-all-links" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white px-4 py-2 text-[10px] font-bold tracking-[0.25em] uppercase">
            Alle Links kopieren
          </button>
          <button onClick={onClose} className="bg-white text-black hover:bg-[#ff0033] hover:text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase">
            Fertig
          </button>
        </div>
      </div>
    </div>
  );
};
